import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { SortDirection } from 'react-virtualized';
import CustomTableCell from './widgets/Cell/TableCell';
import { checkPermission } from '../../../helpers/checkPermissions';
import { makeStyles } from '@material-ui/core';
import { useSelector } from 'react-redux';
import {
  getLocalizedAddress,
  getLocalizedDate,
  getLocalizedLabel,
  getLocalizedDateWithTime,
} from '../../../helpers/localize';
import { RESOURCE_STATUSES_OPTIONS } from '../../Resources/constants';
import {
  CUSTOM_TAGS_OPTIONS,
  PATIENT_CUSTOM_TAGS_OPTIONS,
  SURVEY_TAGS_OPTIONS,
} from '../CustomTags';
import { ALERTS_TYPE_SELECTOR_OPTIONS, ALERT_TAGS_OPTIONS, STATUS_OPTIONS } from '../../Alerts/constants';
import {
  BILLING_CLAIMS_STATUSES_OPTIONS,
  CLAIM_ENTITY_TYPE_SELECTOR_OPTIONS,
  CLAIM_STATUS_SELECTOR_OPTIONS,
  ENCOUNTER_CLAIMS_STATUSES_OPTIONS,
} from '../../Claims/constants';
import { ASSESSMENT_STATUSES_OPTIONS, NO_SHOW_STATUSES_OPTIONS } from '../../Assessments/constants';
import { FORMULA_OPTIONS, ORDER_STATUS_OPTIONS, ORDER_TYPES } from '../../Orders/constants';
import { CLAIM_REPORTS_STATUSES_OPTIONS } from '../../ClaimReport/constants';
import FILTER_TYPES from './widgets/Filters/constants';
import FilterBox from './widgets/Filters/FilterBox';
import cx from 'classnames';
import { BOT_UPLOADS_STATUSES_OPTIONS } from '../../BotActivities/constants';
import { BHI_STATUS_OPTIONS } from '../../Programs/constants';
import CellSimpleText from './widgets/Cell/CellContent/CellSimpleText';
import { TABLE_KEYS } from '../../../redux_store';
import { GENDER_OPTIONS } from '../../PopulationHealth/widgets/PatientDrilldownModal/components/PatientQhsTable';

const getTextWidth = (text, font = '14px Roboto, Helvetica, Arial, sans-serif') => {
  if (!text || typeof text !== 'string') return 0;

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  context.font = font;
  const metrics = context.measureText(text);
  return Math.ceil(metrics.width + 20);
};

// dynamic value
export let TEXT_FILTERS = [
  'customIdentifier',
  'accountCustomIdentifier',
  'address',
  'firstName',
  'lastName',
  'mrnNumber',
  'patientFirstName',
  'patientLastName',
  'patientMrnNumber',
  'viewedBy',
  'acknowledgedBy',
  'uploadFailureReason',
  'providerAssignment',
  'totalAmount',
  'totalCPTs',
  'cptCount',
  'cptPaid',
  'price',
  'paid',
  'icd',
];

export const FILTERING_RULES = {
  exact: 'exact',
  startWith: 'startWith',
  include: 'include',
};

export const SORTING_TYPES = {
  string: 'string',
  number: 'number',
};

function randomIntFromInterval(min, max) {
  // min and max included
  return Math.floor(Math.random() * (max - min + 1) + min);
}

export const getFilteredData = (data, filters, filtersRulesByKey = {}) => {
  const noData = !data || data.length === 0;
  const noFilters =
    filters.reduce((accumulator, { value: currentValue }) => {
      if (Array.isArray(currentValue)) {
        return accumulator + currentValue.join('').trim().length;
      } else {
        return accumulator + currentValue.trim().length;
      }
    }, 0) === 0;

  if (noData) {
    return [];
  } else if (noFilters) {
    return data;
  } else {
    return data.filter(item => {
      let res = true;
      filters.forEach(({ id, value }) => {
        if (res) {
          const currentValue = item[id];
          if (!!value) {
            const filterAsArray = Array.isArray(value) ? value : value.split(',');

            if (filterAsArray.length > 1) {
              res = filterAsArray
                .map(item =>
                  _checkIsMatch(
                    currentValue,
                    item,
                    filtersRulesByKey[id] || { type: FILTERING_RULES.exact }
                  )
                )
                .some(x => x);
            } else {
              res = _checkIsMatch(
                currentValue,
                value,
                filtersRulesByKey[id] || { type: FILTERING_RULES.include }
              );
            }
          }
        }
      });
      return res;
    });
  }
};

const _checkIsMatch = (value, filterValue, filterRules = {}) => {
  if (!filterValue) {
    return true;
  }
  const { type, postfix = '' } = filterRules;
  const test = value.toString().toLowerCase();
  const _filterValue = filterValue.toString().toLowerCase();

  switch (type) {
    case FILTERING_RULES.include: {
      return test.includes(_filterValue);
    }
    case FILTERING_RULES.exact: {
      return value.toString() === filterValue || value === filterValue + postfix;
    }
    case FILTERING_RULES.startWith: {
      return test.startsWith(_filterValue);
    }
  }
};

export const getSortedData = (
  data,
  sort,
  sortingRules = {
    type: SORTING_TYPES.string,
  }
) => {
  if (!data || data.length === 0) {
    return [];
  } else if (!sort || !sort.id) {
    return data;
  }
  const { id: sortBy, desc } = sort;
  const sortDirection = desc ? SortDirection.ASC : SortDirection.DESC;
  const { type } = sortingRules[sortBy];
  return data.sort((a, b) => {
    if (sortDirection === SortDirection.ASC) {
      if (type === SORTING_TYPES.string) {
        return a[sortBy].toString() > b[sortBy].toString();
      } else if (type === SORTING_TYPES.number) {
        return a[sortBy] - b[sortBy];
      }
    } else {
      if (type === SORTING_TYPES.string) {
        return a[sortBy].toString() < b[sortBy].toString();
      } else if (type === SORTING_TYPES.number) {
        return b[sortBy] - a[sortBy];
      }
    }
  });
};

export const currencyFormat = value => {
  return '$ ' + (value ? Number(value) : 0).toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
};

export const useGenerateColumns = (columnsData, tableKey, args, additionalLoadsResults) => {
  const {
    metadata: { currentAccountPermissions },
  } = useSelector(state => state.user);
  const { settings } = useSelector(state => state.table);
  const columnSizes = settings[tableKey] ? settings[tableKey].columnSizes ?? 200 : 200;
  const columnVisibility = settings[tableKey] ? settings[tableKey].columnVisibility ?? {} : {};
  const firstVisibleColumKey = Object.keys(columnVisibility).find(key => columnVisibility[key]);
  const prevRef = useRef();
  const [columns, setColumns] = useState([]);

  const updateColumns = () => {
    const isEqual = JSON.stringify(prevRef.current) === JSON.stringify(additionalLoadsResults);
    if (!isEqual) {
      if (additionalLoadsResults) {
        const columns = Array.isArray(columnsData)
          ? columnsData
          : columnsData(...(args ? Object.values(args) : []));
        setColumns(
          columns
            .filter(item => {
              const hasPermission = item.permission
                ? checkPermission(currentAccountPermissions, item.permission)
                : true;
              return item.hidden !== true && hasPermission;
            })
            .map(
              ({
                header,
                accessorKey,
                permission,
                hidden,
                filter,
                cell,
                enableHiding,
                disableSorting,
                Edit,
                isDefaultCell,
                includeCurrentAccount,
                customAccountUuidForFilter,
              }) => {
                const CustomCell = props => (
                  <Cell
                    isDefaultCell={isDefaultCell}
                    accessorKey={accessorKey}
                    formattedFunction={cell?.formattedFunction}
                    infoPanePage={cell?.infoPanePage}
                    withBubble={cell?.withBubble}
                    withBubbles={cell?.withBubbles}
                    selectorOptionsFromApi={
                      additionalLoadsResults && additionalLoadsResults[accessorKey]
                    }
                    row={props.row?.original}
                    filter={props.table?.options?.state.filter}
                    renderedCellValue={props.renderedCellValue}
                    dataKey={accessorKey}
                    hasManageViewed={
                      firstVisibleColumKey === accessorKey
                        ? props.table?.options?.state?.hasManageViewed
                        : false
                    }
                    canManageViewed={
                      firstVisibleColumKey === accessorKey
                        ? props.table?.options?.state?.canManageViewed
                        : false
                    }
                    showSkeletons={props.table?.options?.state?.canShowSkeletons}
                  />
                );

                const Filter = () => <></>;

                const headerTextWidth = getTextWidth(header);
                const calculatedMinSize = Math.max(100, headerTextWidth + 80);

                const result = {
                  size: columnSizes[accessorKey],
                  header: header,
                  accessorKey: accessorKey,
                  permission: permission,
                  hidden: hidden,
                  filter: filter,
                  Edit: Edit,
                  minSize: calculatedMinSize,
                  maxSize: 500,
                  enableHiding: enableHiding === false ? false : true,
                  enableSorting: !disableSorting,
                  Cell: CustomCell,
                  Filter: Filter,
                };
                if (filter) {
                  if (!filter.filterType || filter.filterType === FILTER_TYPES.PRICE) {
                    TEXT_FILTERS = [...new Set([...TEXT_FILTERS, accessorKey])];
                  }
                  let options = filter.options;
                  if (Object.keys(additionalLoadsResults || {}).includes(accessorKey)) {
                    options = additionalLoadsResults[accessorKey];
                  }
                  result.Filter = props => (
                    <FilterBox
                      {...props}
                      columnData={{
                        ...filter,
                        options: options,
                        isFilterable: true,
                        withBubble: cell?.withBubble,
                        includeCurrentAccount: includeCurrentAccount,
                        customAccountUuidForFilter: customAccountUuidForFilter,
                      }}
                    />
                  );
                }
                return result;
              }
            )
        );
      }
      prevRef.current = additionalLoadsResults;
    }
  };

  useEffect(() => {
    updateColumns();
  }, [...(args ? Object.values(args) : []), additionalLoadsResults]);

  useEffect(() => {
    updateColumns();
  }, []);

  return { columns };
};

export const convertDataFromApi = (
  data,
  tableKey,
  filters,
  additionalKeys,
  additionalLoadsResults
) => {
  return [...(data || [])].map(item => {
    return _convertApiToTableDataValues(
      { ...item, ...additionalKeys },
      tableKey,
      filters,
      additionalLoadsResults
    );
  });
};

const _convertApiToTableDataValues = (obj, tableKey, filters, additionalLoadsResults) => {
  let result = { ...obj };

  Object.keys(obj).forEach(key => {
    if (typeof obj[key] === 'function') {
      if (obj[key](obj) === '') {
        result[key] = '-';
      } else {
        result[key] = obj[key](obj);
      }
      return;
    }

    switch (key) {
      case 'createdTimestamp': {
        if (tableKey === TABLE_KEYS.alertsDSTable || tableKey === TABLE_KEYS.alertsTable) {
          result[key] = obj[key] ? getLocalizedDateWithTime(obj[key]) : '-';
        } else {
          result[key] = obj[key] ? getLocalizedDate(obj[key], 'UTC') : '-';
        }
        break;
      }
      case 'patientDateOfBirth':
      case 'dateOfBirth': {
        const dateValue = obj[key];
        result[key] = dateValue && !dateValue.startsWith('0001-01-01')
          ? getLocalizedDate(dateValue, 'UTC')
          : '-';
        break;
      }
      case 'appointmentTimestamp':
      case 'invitedTimestamp':
      case 'viewedTimestamp':
      case 'engagedTimestamp':
      case 'acknowledgedTimestamp':
      case 'claimGeneratedTimestamp':
      case 'lastModifiedTimestamp':
      case 'entityCompletedTimestamp':
      case 'paymentDate':
      case 'entityOrderTimestamp':
      case 'orderDate':
      case 'reportableGeneratedTimestamp':
      case 'claimReportGeneratedTimestamp':
      case 'dos':
      case 'documentUploadTimestamp':
      case 'programDocDocumentUploadTimestamp':
      case 'orderTimestamp': {
        result[key] = obj[key] ? getLocalizedDate(obj[key], 'UTC') : '-';
        break;
      }
      case 'totalAmount':
      case 'cptCount':
      case 'allowed':
      case 'cptPaid':
      case 'price':
      case 'pr':
      case 'paid':
      case 'totalCPTs': {
        result[key] = obj[key] ? obj[key].toString() : '0';
        break;
      }
      case 'mrnNumber':
      case 'patientMrnNumber':
      case 'patientLastName':
      case 'patientFirstName': {
        result[key] = obj[key] ? obj[key] : '-';
        break;
      }
      case 'address': {
        result.address = obj.address ? getLocalizedAddress(obj.address) : '-';
        break;
      }

      case 'orderType':
      case 'alertType':
      case 'entityType':
      case 'acknowledgeStatus':
      case 'encounterStatus':
      case 'billingStatus':
      case 'claimStatus':
      case 'orderStatus':
      case 'orderFormula':
      case 'botUploadStatus':
      case 'patientGender':
      case 'bhiStatus':
      case 'reportStatus': {
        const options = OPTIONS_BY_KEY[key];
        result[key] = obj[key] ? getLocalizedLabel(options, obj[key], key === 'alertType') : '-';
        break;
      }
      case 'surveyTags': {
        const options = OPTIONS_BY_KEY[key];
        const _filterObj = filters.find(i => i.id === key);
        const filter = _filterObj ? _filterObj.value : [];
        result[key] = obj[key]
          ? obj[key]
              .sort(function (a, b) {
                const _a = filter.includes(a) ? 1 : 0;
                const _b = filter.includes(b) ? 1 : 0;
                return _b - _a;
              })
              .map(item => {
                return getLocalizedLabel(options, item, null, null, true);
              })
          : '-';
        break;
      }
      case 'accountCustomTags':
      case 'patientCustomTags':
      case 'customTags': {
        const _filterObj = filters.find(i => i.id === key);
        const filter = _filterObj ? _filterObj.value : [];
        const isPatientOptions =
          key === 'patientCustomTags' ||
          (key === 'customTags' &&
            (tableKey === TABLE_KEYS.patientsDSTable || tableKey === TABLE_KEYS.patientsTable));
        result[key] = obj[key]
          ? obj[key]
              .sort(function (a, b) {
                const _a = filter.includes(a) ? 1 : 0;
                const _b = filter.includes(b) ? 1 : 0;
                return _b - _a;
              })
              .map(item => {
                return getLocalizedLabel(
                  isPatientOptions ? PATIENT_CUSTOM_TAGS_OPTIONS : CUSTOM_TAGS_OPTIONS,
                  item
                );
              })
          : '-';
        break;
      }
      case 'status': {
        if (tableKey === TABLE_KEYS.resourcesDSTable || tableKey === TABLE_KEYS.resourcesTable) {
          result.status = getLocalizedLabel(RESOURCE_STATUSES_OPTIONS, obj.status) || '-';
        }
        if (
          tableKey === TABLE_KEYS.assessmentsDSTable ||
          tableKey === TABLE_KEYS.assessmentsTable ||
          tableKey === TABLE_KEYS.botActivitiesDSTable ||
          tableKey === TABLE_KEYS.botActivitiesTable
        ) {
          result.status = getLocalizedLabel(ASSESSMENT_STATUSES_OPTIONS, obj.status) || '-';
        }
        break;
      }
      case 'noShowStatus': {
        if (
          tableKey === TABLE_KEYS.assessmentsDSTable ||
          tableKey === TABLE_KEYS.assessmentsTable
        ) {
          result.noShowStatus = getLocalizedLabel(NO_SHOW_STATUSES_OPTIONS, obj.noShowStatus) || '-';
        }
        break;
      }
      case 'title': {
        if (tableKey === TABLE_KEYS.resourcesDSTable || tableKey === TABLE_KEYS.resourcesTable) {
          result.resource = obj.title ? obj.title : '-';
        }
        break;
      }
      case 'accountCustomIdentifier':
      case 'customIdentifier': {
        result[key] = obj[key] ? obj[key] : 'N/A';
        break;
      }
      case 'claimable': {
        result.claimable = obj.result === false;
        break;
      }
      case 'createdBy': {
        result.createdBy =
          obj.createdBy === 'manuallyCreated'
            ? 'Manually Created'
            : obj.createdBy === 'integration'
            ? 'Integration'
            : obj.createdBy === 'automated'
            ? 'Automated'
            : obj.createdBy || '-';
        break;
      }
      case 'resourceName': {
        result.resourceName = {
          resourceName: obj.resourceName || '-',
          resourceType: obj.resourceType || '-',
        };
        break;
      }
      case 'alertTags': {
        const _filterObj = filters.find(i => i.id === key);
        const filter = _filterObj ? _filterObj.value : [];
        result.alertTags = obj.alertTags
          ? obj.alertTags
              .sort(function (a, b) {
                const _a = filter
                  .map(item => item.replace(' ', '').toLowerCase())
                  .includes(a.replace(' ', '').toLowerCase())
                  ? 1
                  : 0;
                const _b = filter
                  .map(item => item.replace(' ', '').toLowerCase())
                  .includes(b.replace(' ', '').toLowerCase())
                  ? 1
                  : 0;
                return _b - _a;
              })
              .map(item => {
                let alertTag = item;
                if (
                  alertTag === 'SUICIDE OR SELF HARM' ||
                  alertTag === 'SUICIDE OF SELF HARM' ||
                  alertTag === 'Suicide or Self Harm Risk' ||
                  alertTag === 'Suicide of Self Harm' ||
                  alertTag === 'Suicide or Self Harm-Risk'
                ) {
                  alertTag = 'Suicide or Self-Harm Risk';
                }
                const backendOptions =
                  additionalLoadsResults && additionalLoadsResults[key]
                    ? additionalLoadsResults[key]
                    : [];
                // Merge backend-supplied options with the frontend
                // fallback list so newly-added alert tags (e.g.
                // PHQ-GAD16 / Q-Lung taxonomy) render with proper
                // labels even before /accounts/alerts/tags returns
                // them. Backend entries win on collision; static
                // entries fill the gap.
                const seenValues = new Set(backendOptions.map(o => o?.value));
                const mergedOptions = [
                  ...backendOptions,
                  ...ALERT_TAGS_OPTIONS.filter(o => !seenValues.has(o.value)),
                ];
                return getLocalizedLabel(mergedOptions, alertTag, true, '@@@') || '-';
              })
          : [];

        break;
      }
    }
  });

  return result;
};

const OPTIONS_BY_KEY = {
  patientCustomTags: PATIENT_CUSTOM_TAGS_OPTIONS,
  accountCustomTags: CUSTOM_TAGS_OPTIONS,
  entityType: CLAIM_ENTITY_TYPE_SELECTOR_OPTIONS,
  alertType: ALERTS_TYPE_SELECTOR_OPTIONS,
  acknowledgeStatus: STATUS_OPTIONS,
  surveyTags: SURVEY_TAGS_OPTIONS,
  reportStatus: CLAIM_REPORTS_STATUSES_OPTIONS,
  orderFormula: FORMULA_OPTIONS,
  orderType: ORDER_TYPES,
  orderStatus: ORDER_STATUS_OPTIONS,
  claimStatus: CLAIM_STATUS_SELECTOR_OPTIONS,
  billingStatus: BILLING_CLAIMS_STATUSES_OPTIONS,
  encounterStatus: ENCOUNTER_CLAIMS_STATUSES_OPTIONS,
  botUploadStatus: BOT_UPLOADS_STATUSES_OPTIONS,
  patientGender: GENDER_OPTIONS,
  bhiStatus: BHI_STATUS_OPTIONS,
};

const Cell = memo(
  ({
    formattedFunction,
    withBubble,
    withBubbles,
    row,
    canManageViewed,
    renderedCellValue,
    infoPanePage,
    hasManageViewed,
    filter,
    isDefaultCell,
    accessorKey,
    showSkeletons,
    selectorOptionsFromApi,
  }) => {
    const currentValue = useMemo(
      () =>
        formattedFunction
          ? formattedFunction(renderedCellValue) || '-'
          : !renderedCellValue || (Array.isArray(renderedCellValue) && !renderedCellValue.length)
          ? '-'
          : renderedCellValue,
      [renderedCellValue]
    );

    return row.showShimmer || showSkeletons ? (
      <_Shimmer />
    ) : (isDefaultCell ||
        !currentValue ||
        currentValue === '-' ||
        (Array.isArray(currentValue) && !currentValue.length)) &&
      !hasManageViewed ? (
      <CellSimpleText value={currentValue} />
    ) : (
      <CustomTableCell
        hasManageViewed={hasManageViewed}
        canManageViewed={canManageViewed}
        infoPanePage={infoPanePage}
        selectorOptionsFromApi={selectorOptionsFromApi}
        withBubble={withBubble}
        withBubbles={withBubbles}
        row={{
          errorType: row?.errorType,
          uuid: row?.uuid,
          accountUUID: row?.accountUUID,
          patientUUID: row?.patientUUID ?? row?.uuid,
          patientFirstName: row?.patientFirstName ?? row?.firstName,
          patientLastName: row?.patientLastName ?? row?.lastName,
        }}
        renderedCellValue={currentValue}
        dataKey={accessorKey}
        filter={filter}
      />
    );
  }
);

const useCallStyles = makeStyles(theme => ({
  show: {
    maxWidth: '100%',
    height: '1.25rem',
    maxHeight: 20,
    borderRadius: '0.25rem',
    display: 'flex',
    backgroundColor: '#dee2e6',
    visibility: 'visible',
  },
}));

const _Shimmer = memo(() => {
  const rndInt = randomIntFromInterval(50, 80);
  const classes = useCallStyles();

  return <div style={{ minWidth: rndInt + '%', height: 21 }} className={cx(classes.show)} />;
});

export function useForceRepaintOnVisibility(selector) {
  useEffect(() => {
    const forceRepaint = () => {
      if (document.visibilityState === 'visible') {
        requestAnimationFrame(() => {
          const el = document.querySelector(selector);
          if (el) {
            el.style.display = 'none';
            void el.offsetHeight;
            el.style.display = '';
          }
        });
      }
    };

    document.addEventListener('visibilitychange', forceRepaint);
    return () => document.removeEventListener('visibilitychange', forceRepaint);
  }, [selector]);
}
